import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { FileService } from "../services/fileService.js";
import { CampaignService } from "../services/campaignService.js";
import formErrorPopupInstance from "../utils/formValidation.js";
import SweetAlertService from "../services/SweetAlertService";
import functionConstants from "../utils/functionConstants.js";

const CreateCampaignForm = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFormValid, setIsFormValid] = useState(true); // Initialize to true
  const functionId = localStorage.getItem("functionId");
  const functionList = String(functionId).split(",");
  const { t, i18n } = useTranslation();
  const [isMobileScreen, setIsMobileScreen] = useState(
    window.innerWidth <= 1250 ? true : false
  );
  const [IFACA, setIFACA] = useState("IFA&CA");
  const [template, setTemplate] = useState("");
  const [thumbnailId, setThumbnailId] = useState("");
  const [fileName, setFileName] = useState("");
  const [values, setValues] = useState({
    campaignNameEng: "",
    campaignCode: "",
    campaignNameZHTW: "",
    campaignStartDate: "",
    campaignNameZHCN: "",
    campaignEndDate: "",
    remark: "",
    thumbnailDocID: "",
  });

  const handleFileInputChange = async (event) => {
    const loadingSwal = SweetAlertService.showLoading(t("loginPage.loading"));
    const file = event.target.files[0];
    var fileService = new FileService();
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        const base64String = result.split(",")[1];
        fileService.upload(file.name, base64String, "c").then((value) => {
          setThumbnailId(value.data.referenceId);
          setFileName(file.name);
          SweetAlertService.close(loadingSwal);
          return value.data;
        });
      };
    } else {
      SweetAlertService.close(loadingSwal);
    }
  };

  const handleIFACAChange = (event) => {
    setIFACA(event.target.value);
  };

  const handleTemplateChange = (event) => {
    setTemplate(event.target.value);
  };

  const handleChange = (event) => {
    event.target.value.trim();
    const { name, value } = event.target;
    if (name === "campaignStartDate") {
      handleStartDateChange(event);
    }
    if (name === "campaignEndDate") {
      handleEndDateChange(event);
    }

    setValues({ ...values, [name]: value });
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    if (endDate && newStartDate > endDate) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    if (startDate && newEndDate < startDate) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  const goBack = () => {
    window.location.href = "/Campaign";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      values.campaignNameZHCN === undefined ||
      values.campaignNameZHCN === null
    ) {
      values.campaignNameZHCN = "";
    }

    if (values.thumbnailDocID === undefined || values.thumbnailDocID === null) {
      values.thumbnailDocID = "";
    }

    if (values.remark === undefined || values.remark === null) {
      values.remark = "";
    }

    if (values.file === undefined || values.file === null) {
      values.file = "";
    }

    if (values.campaignNameEng === "" || values.campaignNameEng === null) {
      formErrorPopupInstance.addError(t("campaignPage.campaignNameEng"));
    }

    if (values.campaignNameZHTW === "" || values.campaignNameZHTW === null) {
      formErrorPopupInstance.addError(t("campaignPage.campaignNameHk"));
    }

    if (values.campaignNameZHCN === "" || values.campaignNameZHCN === null) {
      formErrorPopupInstance.addError(t("campaignPage.campaignNameCn"));
    }

    if (values.campaignCode === "" || values.campaignCode === null) {
      formErrorPopupInstance.addError(t("campaignPage.campaignCode"));
    }

    if (IFACA === "") {
      formErrorPopupInstance.addError(t("common.ifaCa"));
    }

    if (template === "") {
      formErrorPopupInstance.addError(t("campaignPage.template"));
    }

    if (!isFormValid || startDate === "" || endDate === "") {
      formErrorPopupInstance.addError(t("errors.invalidDateRange"));
    }

    formErrorPopupInstance.showErrors();

    if (formErrorPopupInstance.errors.length === 0) {
      e.preventDefault();
      try {
        var campaignService = new CampaignService();
        campaignService
          .retrieveCampaignHeader(
            0,
            "create",
            values,
            thumbnailId,
            IFACA,
            template
          )
          .then((value) => {
            if (value.status === 200) {
              Swal.fire({
                icon: "success",
                title: t("campaignPage.campaignCreated"),
                showConfirmButton: false,
                timer: 1700,
              }).then(function () {
                window.location = "/Campaign";
              });
            }
          });
      } catch (error) {}
    }
    formErrorPopupInstance.clearErrors();
  };

  useEffect(() => {
    const savedLanguage = Cookies.get("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    function handleResize() {
      setIsMobileScreen(window.innerWidth <= 1250);
    }

    handleResize();
    window.addEventListener("resize", handleResize);

    // Add this block to update isMobileScreen when the screen size is larger than 1207 pixels
    return () => {
      window.removeEventListener("resize", handleResize);
      setIsMobileScreen(false);
    };
  }, []);

  return (
    <>
      {isMobileScreen ? (
        <div>
          <form className="border border-red rounded " onSubmit={handleSubmit}>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignNameEng">
                  {t("campaignPage.campaignNameEng")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignNameEng"
                  name="campaignNameEng"
                  value={values.campaignNameEng}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignNameZHTW">
                  {t("campaignPage.campaignNameHk")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignNameZHTW"
                  name="campaignNameZHTW"
                  value={values.campaignNameZHTW}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignNameZHCN">
                  {t("campaignPage.campaignNameCn")}
                </label>
                <span className="text-red-600">*</span>
                <input
                  type="text"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignNameZHCN"
                  name="campaignNameZHCN"
                  value={values.campaignNameZHCN}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignCode">
                  {t("campaignPage.campaignCode")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignCode"
                  name="campaignCode"
                  value={values.campaignCode}
                  onChange={handleChange}
                  n
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignStartDate">
                  {t("common.startDate")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignStartDate"
                  name="campaignStartDate"
                  value={values.campaignStartDate}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="campaignEndDate">
                  {t("common.endDate")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="campaignEndDate"
                  name="campaignEndDate"
                  value={values.campaignEndDate}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex mx-3 my-5 hidden">
              <div className="px-4 w-full">
                <label htmlFor="IFA/CA">
                  {t("common.ifaCa")}
                  <span className="text-red-600">*</span>
                </label>
                <select
                  id="IFACA"
                  aria-label="Select IFA/CA"
                  className="w-full bg-gray-100 border border-gray-100 text-gray-900 text-sm rounded-lg focus:ring-ft-light focus:border-ft-light p-2.5"
                  value={"IFA&CA"}
                  name="ifaCaIndicator"
                  onChange={handleIFACAChange}
                  disabled>
                  {/* <option value="" className="text-word " disabled>
                    {t("cms.select") + t("common.ifaCa")}
                  </option> */}
                  <option value="IFA&CA">{t("common.ifaAndCa")}</option>
                  <option value="IFA">{t("common.ifa")}</option>
                  <option value="CA">{t("common.ca")}</option>
                </select>
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="Template">
                  {t("campaignPage.template")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <select
                  id="template"
                  aria-label="Select template"
                  className="w-full bg-white-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-ft-light focus:border-ft-light p-2.5"
                  value={template}
                  name="template"
                  onChange={handleTemplateChange}>
                  <option value="" className="text-word " disabled>
                    {t("cms.select") + t("campaignPage.template")}
                  </option>
                  {functionList.includes(
                    String(functionConstants.CAMPAIGN_HEADER_KDOLLAR_CREATE)
                  ) ? (
                    <option value="kdollar">
                      {t("campaignPage.template.kDollar")}
                    </option>
                  ) : (
                    <></>
                  )}
                  {functionList.includes(
                    String(functionConstants.CAMPAIGN_HEADER_CICONV_CREATE)
                  ) ? (
                    <option value="ciconv">
                      {t("campaignPage.template.ciConv")}
                    </option>
                  ) : (
                    <></>
                  )}
                </select>
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="remark">{t("campaignPage.remarks")}</label>
                <textarea
                  type="text"
                  className="form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="remark"
                  name="remark"
                  value={values.remark}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex m-3">
              <div className="px-4 w-full">
                <label htmlFor="file-upload" className="upload-label mr-3">
                  {t("common.browseUpload")}
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden "
                />
                <label
                  htmlFor="file-upload"
                  className="upload-label mr-3 bg-ft-light text-white rounded py-3 px-4 hover:bg-ft cursor-pointer">
                  {t("common.selectFile")}
                </label>
                <label htmlFor="file-upload" className="upload-label mr-3 ">
                  {fileName ? fileName : t("common.noFile")}
                </label>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end">
              <button
                type="Submit"
                className="ml-4 mr-2 text-white bg-ft-light hover:bg-ft rounded-md px-4 py-2 active:bg-white active:text-ft active:ring-1 active:ring-ft transition">
                {t("common.save")}
              </button>
              <button
                onClick={goBack}
                type="button"
                className="text-ft-light ring-1 ring-ft-light bg-white hover:bg-ft hover:text-white rounded-md px-4 py-2 active:bg-white active:text-red-500 active:ring-1 active:ring-red-500 transition">
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div>
          <form
            className="border border-red rounded p-1 text-content"
            onSubmit={handleSubmit}>
            <div className="form-row flex mx-3 my-5">
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12 ">
                  <label htmlFor="campaignNameEng">
                    {t("campaignPage.campaignNameEng")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignNameEng"
                    name="campaignNameEng"
                    value={values.campaignNameEng}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignNameZHTW">
                    {t("campaignPage.campaignNameHk")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignNameZHTW"
                    name="campaignNameZHTW"
                    value={values.campaignNameZHTW}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {/* <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignCode">
                    {t("campaignPage.campaignCode")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignCode"
                    name="campaignCode"
                    value={values.campaignCode}
                    onChange={handleChange}
                  />
                </div>
              </div> */}
            </div>
            <div className="form-row flex mx-3 my-5">
              {/* <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignNameZHTW">
                    {t("campaignPage.campaignNameHk")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignNameZHTW"
                    name="campaignNameZHTW"
                    value={values.campaignNameZHTW}
                    onChange={handleChange}
                  />
                </div>
              </div> */}
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignNameZHCN">
                    {t("campaignPage.campaignNameCn")}
                  </label>
                  <span className="text-red-600">*</span>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignNameZHCN"
                    name="campaignNameZHCN"
                    value={values.campaignNameZHCN}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignCode">
                    {t("campaignPage.campaignCode")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignCode"
                    name="campaignCode"
                    value={values.campaignCode}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {/* <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignStartDate">
                    {t("common.startDate")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignStartDate"
                    name="campaignStartDate"
                    value={values.campaignStartDate}
                    onChange={handleChange}
                  />
                </div>
              </div> */}
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignStartDate">
                    {t("common.startDate")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignStartDate"
                    name="campaignStartDate"
                    value={values.campaignStartDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {/* <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignNameZHCN">
                    {t("campaignPage.campaignNameCn")}
                  </label>
                  <span className="text-red-600">*</span>
                  <input
                    type="text"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignNameZHCN"
                    name="campaignNameZHCN"
                    value={values.campaignNameZHCN}
                    onChange={handleChange}
                  />
                </div>
              </div> */}
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="campaignEndDate">
                    {t("common.endDate")}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                    id="campaignEndDate"
                    name="campaignEndDate"
                    value={values.campaignEndDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12">
                  <label htmlFor="Template">
                    {t("campaignPage.template")}{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="template"
                    aria-label="Select template"
                    className="text-content w-full bg-white-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-ft-light focus:border-ft-light p-2.5"
                    value={template}
                    name="template"
                    onChange={handleTemplateChange}>
                    <option value="" className="text-word " disabled>
                      {t("cms.select") + t("campaignPage.template")}
                    </option>
                    {functionList.includes(
                      String(functionConstants.CAMPAIGN_HEADER_KDOLLAR_CREATE)
                    ) ? (
                      <option value="kdollar">
                        {t("campaignPage.template.kDollar")}
                      </option>
                    ) : (
                      <></>
                    )}
                    {functionList.includes(
                      String(functionConstants.CAMPAIGN_HEADER_CICONV_CREATE)
                    ) ? (
                      <option value="ciconv">
                        {t("campaignPage.template.ciConv")}
                      </option>
                    ) : (
                      <></>
                    )}
                  </select>
                </div>
              </div>
              <div className="form-group w-1/2 flex justify-center">
                <div className="w-11/12 hidden">
                  <label htmlFor="IFA/CA">
                    {t("common.ifaCa")}
                    <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="IFACA"
                    aria-label="Select IFA/CA"
                    className="text-content w-full bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-ft-light focus:border-ft-light p-2.5"
                    value={"IFA&CA"}
                    name="ifaCaIndicator"
                    onChange={handleIFACAChange}
                    disabled>
                    {/* <option value="" className="text-word " disabled>
                      {t("cms.select") + t("common.ifaCa")}
                    </option> */}
                    <option value="IFA&CA">{t("common.ifaAndCa")}</option>
                    <option value="IFA">{t("common.ifa")}</option>
                    <option value="CA">{t("common.ca")}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-row flex mx-3 my-5">
              <div className="px-4 w-full">
                <label htmlFor="remark">{t("campaignPage.remarks")}</label>
                <textarea
                  type="text"
                  className="text-content form-control ring-0 hover:border-ft-light active:border-ft-light focus:border-ft-light"
                  id="remark"
                  name="remark"
                  value={values.remark}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row flex m-3">
              <div className="px-4 w-full">
                <label htmlFor="file-upload" className="upload-label mr-3">
                  {t("common.browseUpload")}
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="upload-label mr-3 bg-ft-light text-white rounded py-3 px-4 hover:bg-ft cursor-pointer">
                  {t("common.selectFile")}
                </label>
                <label htmlFor="file-upload" className="upload-label mr-3 ">
                  {fileName ? fileName : t("common.noFile")}
                </label>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end">
              <button
                type="Submit"
                className="ml-4 mr-3 text-white bg-ft-light hover:bg-ft rounded-md px-4 py-2 active:bg-white active:text-ft active:ring-1 active:ring-ft transition">
                {t("common.save")}
              </button>
              <button
                onClick={goBack}
                type="button"
                className="text-ft-light ring-1 ring-ft-light bg-white hover:bg-ft hover:text-white rounded-md px-4 py-2 active:bg-white active:text-red-500 active:ring-1 active:ring-red-500 transition">
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CreateCampaignForm;
